import { inject, Service } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
// ɵ-prefixed NGXS exports carry no stability guarantee. Aliased here so an NGXS bump that renames
// or drops it is a one-line fix in this file rather than a change at every use site.
import { ɵStoreOptions as StoreOptions } from '@ngxs/store/internals';
import { CollectionStateBase } from '@portal-core/data/collection/services/collection-state.base';
import { McModel } from '@portal-core/data/common/models/mc-model.model';
import { ModelId } from '@portal-core/data/common/types/mc-model.type';
import { defaultsDeep, omit } from 'lodash';
import { Observable, map } from 'rxjs';

// Types and Interfaces
export type PageFilterValue = number | string;

export interface PageFilterMap {
  [name: string]: PageFilterValue;
}

// The page state's model class
export class PageStateModel {
  itemId: ModelId;
  subItemId: ModelId;
  loading: boolean;
  errorLoading: unknown;
  filters: PageFilterMap;
}

// Define the interface for the model's defaults. Every property is optional.
export interface PageStateModelDefaults {
  itemId?: ModelId;
  subItemId?: ModelId;
  loading?: boolean;
  errorLoading?: unknown;
  filters?: PageFilterMap;
}

// The action classes created by the @PageState decorator, exposed on the state's static `actions`.
export interface PageStateActions {
  Reset: new () => object;
  SetItemId: new (payload: { itemId: ModelId }) => object;
  SetSubItemId: new (payload: { subItemId: ModelId }) => object;
  SetLoading: new (payload: { loading: boolean }) => object;
  SetErrorLoading: new (payload: { errorLoading: unknown }) => object;
  SetFilters: new (payload: { filters: PageFilterMap }) => object;
  SetFilterByName: new (payload: { name: string; value: PageFilterValue }) => object;
}

// Define the options for the state decorator
export interface PageStateOptions extends StoreOptions<PageStateModelDefaults> {
  actionName: string;
}

// This base class is only used to give a proper interface for TypeScript. The methods are all overridden by the PageState decorator.
export abstract class PageStateBase {
  static actions: PageStateActions;
  static itemId(_state: PageStateModel): ModelId { return null; }
  static subItemId(_state: PageStateModel): ModelId { return null; }
  static loading(_state: PageStateModel): boolean { return null; }
  static errorLoading(_state: PageStateModel): unknown { return null; }
  static filters(_state: PageStateModel): PageFilterMap { return null; }
  static filterByName<V>(_state: PageStateModel): (name: string) => V { return null; }
  setItemId(_ctx: StateContext<PageStateModel>, _action: unknown) { }
  setSubItemId(_ctx: StateContext<PageStateModel>, _action: unknown) { }
  setLoading(_ctx: StateContext<PageStateModel>, _action: unknown) { }
  setErrorLoading(_ctx: StateContext<PageStateModel>, _action: unknown) { }
  setFilters(_ctx: StateContext<PageStateModel>, _action: unknown) { }
}

// Class decorator that will modify a class to be an NGXS State that holds a page's data
export function PageState(options: PageStateOptions) {
  // `constructor` is the decorated class, used below as the base of the generated state class
  // (`class PState extends constructor`). TypeScript requires a mixin base to be typed `any`
  // (TS2545), so this any is mandated rather than a shortcut.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function (constructor: any) {
    // Create the actions for the page state
    class Reset {
      static readonly type = `[${options.actionName}] Reset`;
    }

    class SetItemId {
      static readonly type = `[${options.actionName}] SetItemId`;
      constructor(public payload: { itemId: ModelId }) { }
    }

    class SetSubItemId {
      static readonly type = `[${options.actionName}] SetSubItemId`;
      constructor(public payload: { subItemId: ModelId }) { }
    }

    class SetLoading {
      static readonly type = `[${options.actionName}] SetLoading`;
      constructor(public payload: { loading: boolean }) { }
    }

    class SetErrorLoading {
      static readonly type = `[${options.actionName}] SetErrorLoading`;
      constructor(public payload: { errorLoading: unknown }) { }
    }

    class SetFilters {
      static readonly type = `[${options.actionName}] SetFilters`;
      constructor(public payload: { filters: PageFilterMap }) { }
    }

    class SetFilterByName {
      static readonly type = `[${options.actionName}] SetFilterByName`;
      constructor(public payload: { name: string, value: PageFilterValue }) { }
    }

    const stateDefaults: PageStateModel = {
      itemId: null,
      subItemId: null,
      loading: false,
      errorLoading: null,
      filters: {}
    };

    // Create a new State class that extends the original class
    @State<PageStateModel>(defaultsDeep(options, {
      defaults: stateDefaults
    }))
    @Service({ autoProvided: false })
    class PState extends constructor {
      static actions = { Reset, SetItemId, SetSubItemId, SetLoading, SetErrorLoading, SetFilters, SetFilterByName };

      @Selector()
      static itemId(state: PageStateModel): ModelId { return state.itemId; }

      @Selector()
      static subItemId(state: PageStateModel): ModelId { return state.subItemId; }

      @Selector()
      static loading(state: PageStateModel): boolean { return state.loading; }

      @Selector()
      static errorLoading(state: PageStateModel): unknown { return state.errorLoading; }

      @Selector()
      static filters(state: PageStateModel): PageFilterMap { return state.filters; }

      @Selector()
      static filterByName<V>(state: PageStateModel): (name: string) => V {
        return (name: string): V => {
          return state.filters[name] as unknown as V; // Cast through unknown: the stored PageFilterValue is surfaced as the caller-chosen V, which the public interface keeps strongly typed.
        };
      }

      @Action(Reset)
      reset(ctx: StateContext<PageStateModel>) {
        ctx.patchState(omit(stateDefaults, 'filters'));
      }

      @Action(SetItemId)
      setItemId(ctx: StateContext<PageStateModel>, action: SetItemId) {
        ctx.patchState({
          itemId: action.payload.itemId
        });
      }

      @Action(SetSubItemId)
      setSubItemId(ctx: StateContext<PageStateModel>, action: SetSubItemId) {
        ctx.patchState({
          subItemId: action.payload.subItemId
        });
      }

      @Action(SetLoading)
      setLoading(ctx: StateContext<PageStateModel>, action: SetLoading) {
        ctx.patchState({
          loading: action.payload.loading
        });
      }

      @Action(SetErrorLoading)
      setErrorLoading(ctx: StateContext<PageStateModel>, action: SetErrorLoading) {
        ctx.patchState({
          errorLoading: action.payload.errorLoading
        });
      }

      @Action(SetFilters)
      setFilters(ctx: StateContext<PageStateModel>, action: SetFilters) {
        ctx.patchState({
          filters: action.payload.filters
        });
      }

      @Action(SetFilterByName)
      setFilterByName(ctx: StateContext<PageStateModel>, action: SetFilterByName) {
        ctx.patchState({
          filters: {
            ...ctx.getState().filters,
            [action.payload.name]: action.payload.value
          }
        });
      }
    }

    return PState;
  };
}

/*
 * PageDataServiceBase
 */
@Service({ autoProvided: false })
export abstract class PageDataServiceBase<T = unknown, S = unknown> {
  // NGXS selector functions, assigned by the @PageState decorator below and handed straight to
  // store.select / store.selectSnapshot. Their argument list is decided by the @Selector deps.
  static item: (...args: unknown[]) => unknown;
  static subItem: (...args: unknown[]) => unknown;
  state: typeof PageStateBase;

  protected readonly store = inject(Store);

  reset$(): Observable<void> {
    return this.store.dispatch(new this.state.actions.Reset());
  }

  // ItemId
  getItemId(): ModelId {
    return this.store.selectSnapshot(this.state.itemId);
  }

  getItemId$(): Observable<ModelId> {
    return this.store.select(this.state.itemId);
  }

  setItemId$(itemId: ModelId): Observable<void> {
    return this.store.dispatch(new this.state.actions.SetItemId({ itemId }));
  }

  // SubItemId
  getSubItemId(): ModelId {
    return this.store.selectSnapshot(this.state.subItemId);
  }

  getSubItemId$(): Observable<ModelId> {
    return this.store.select(this.state.subItemId);
  }

  setSubItemId$(subItemId: ModelId): Observable<void> {
    return this.store.dispatch(new this.state.actions.SetSubItemId({ subItemId }));
  }

  // Loading
  getLoading(): boolean {
    return this.store.selectSnapshot(this.state.loading);
  }

  getLoading$(): Observable<boolean> {
    return this.store.select(this.state.loading);
  }

  setLoading$(loading: boolean): Observable<void> {
    return this.store.dispatch(new this.state.actions.SetLoading({ loading }));
  }

  // Error Loading
  getErrorLoading(): unknown {
    return this.store.selectSnapshot(this.state.errorLoading);
  }

  getErrorLoading$(): Observable<unknown> {
    return this.store.select(this.state.errorLoading);
  }

  setErrorLoading$(errorLoading: unknown): Observable<void> {
    return this.store.dispatch(new this.state.actions.SetErrorLoading({ errorLoading }));
  }

  // Filters
  getFilters(): PageFilterMap {
    return this.store.selectSnapshot(this.state.filters);
  }

  getFilters$(): Observable<PageFilterMap> {
    return this.store.select(this.state.filters);
  }

  setFilters$(filters: PageFilterMap): Observable<void> {
    return this.store.dispatch(new this.state.actions.SetFilters({ filters }));
  }

  // Filter by Name
  getFilterByName<V>(name: string): V {
    return this.store.selectSnapshot<PageFilterMap>(this.state.filters)[name] as unknown as V;
  }

  getFilterByName$<V>(name: string): Observable<V> {
    return this.store.select<(name: string) => V>(this.state.filterByName).pipe(
      map(fn => fn(name))
    );
  }

  setFilterByName$(name: string, value: PageFilterValue): Observable<void> {
    return this.store.dispatch(new this.state.actions.SetFilterByName({ name, value }));
  }

  // Item - defined by the PageStateService decorator
  getItem(): T { return null; }
  getItem$(): Observable<T> { return null; }

  // SubItem - defined by the PageStateService decorator
  getSubItem(): S { return null; }
  getSubItem$(): Observable<S> { return null; }
}

interface PageStateServiceOptions {
  pageState: typeof PageStateBase;
  itemCollectionState: typeof CollectionStateBase;
  subItemCollectionState?: typeof CollectionStateBase;
}

/**
 * The constructor shape the @PageStateService decorator mutates: any PageDataServiceBase subclass,
 * carrying the writable static selector fields and the prototype the decorator assigns to. Written
 * structurally (rather than `typeof PageDataServiceBase`) so concrete, non-generic subclass
 * constructors remain assignable now that PageDataServiceBase is generic.
 */
interface DecoratablePageDataService {
  new (...args: never[]): PageDataServiceBase;
  // Method signatures (bivariant params) so the NGXS @Selector functions assigned to them may declare their own narrower parameter types.
  item(...args: unknown[]): unknown;
  subItem(...args: unknown[]): unknown;
  prototype: PageDataServiceBase;
}

export function PageStateService<T extends McModel, S extends McModel = McModel>(options: PageStateServiceOptions) {
  return function (constructor: DecoratablePageDataService): void {
    // Create a new data service class that extends the original class
    // Gives the class functions for getting the item and sub item for the current page
    class Selectors {
      @Selector([options.pageState.itemId, options.itemCollectionState.itemsSelector])
      static item(itemId: ModelId, items: Dictionary) {
        if (items && itemId !== null && typeof itemId !== 'undefined') {
          return items[itemId];
        }
      }

      @Selector([options.pageState.subItemId, options.subItemCollectionState ? options.subItemCollectionState.itemsSelector : undefined])
      static subItem(subItemId: ModelId, items: Dictionary) {
        if (items && subItemId !== null && typeof subItemId !== 'undefined') {
          return items[subItemId];
        }
      }
    }

    constructor.item = Selectors.item;
    constructor.subItem = Selectors.subItem;

    constructor.prototype.getItem = function getItem(): T {
      return this.store.selectSnapshot(constructor.item) as T;
    };

    constructor.prototype.getItem$ = function getItem$(): Observable<T> {
      return this.store.select(constructor.item) as Observable<T>;
    };

    constructor.prototype.getSubItem = function getSubItem(): S {
      return this.store.selectSnapshot(constructor.subItem) as S;
    };

    constructor.prototype.getSubItem$ = function getSubItem$(): Observable<S> {
      return this.store.select(constructor.subItem) as Observable<S>;
    };
  };
}
